import { CharacterStateBase } from './_stateLibrary';
import { ICharacterState } from '../../interfaces/ICharacterState';
import { Character } from '../Character';
import { CharacterAnimation } from '../../enums/CharacterAnimation';
import { Npc } from '../Npc';
import * as THREE from 'three';
import { BloodEffect } from '../particles/BloodEffect';
import { FiringEffect } from '../particles/FiringEffect';
import { PistolShootAudio } from './audio/PistolShootAudio';

export class PistolShoot extends CharacterStateBase implements ICharacterState {
    private rayCaster: THREE.Raycaster;
    private hasFired = false;
    private audio: PistolShootAudio | null = null;

    constructor(character: Character) {
        super(character);
        this.rayCaster = new THREE.Raycaster();
        this.character.simulation.velocitySimulator.damping = 0.6;
        this.character.simulation.velocitySimulator.mass = 10;
        this.character.simulation.setArcadeVelocityTarget(0);
        this.playAnimation(CharacterAnimation.PistolShoot, 0.1, true, true);

        if (character.world?.audioManager) {
            this.audio = new PistolShootAudio(character.world.audioManager, character);
        }
    }

    public exit(): void {
        this.audio?.stop();
    }

    public update(timeStep: number): void {
        super.update(timeStep);
        this.onDie();
        if (!this.hasFired) {
            this.hasFired = true;
            this.audio?.play();
            this.handleImpact();
        }
    }

    private handleImpact(): void {
        const pistol = this.character.getObjectByName('pistol') as THREE.Object3D | undefined;

        const firingEffect = new FiringEffect(this.character.world);
        if (pistol) { firingEffect.triggerFiringFire(pistol); }

        if (this.character instanceof Npc) {
            const origin = new THREE.Vector3();
            if (pistol) {
                pistol.getWorldPosition(origin);
            } else {
                this.character.getWorldPosition(origin);
            }

            const direction = new THREE.Vector3();
            this.character.getWorldDirection(direction).normalize();
            const shiftedOrigin = origin.clone().add(direction.clone().multiplyScalar(0.35));

            this.rayCaster.near = 0.05;
            this.rayCaster.far = 200;
            this.rayCaster.set(shiftedOrigin, direction);
        } else {
            this.rayCaster.setFromCamera(new THREE.Vector2(0, 0), this.character.world.camera);
            this.rayCaster.near = 0;
            this.rayCaster.far = 1000;
        }

        const characterRoots = this.character.world.characters as unknown as THREE.Object3D[];
        const intersects = this.rayCaster.intersectObjects(characterRoots, true).filter(hit =>
            !this.isDescendantOf(hit.object, this.character as unknown as THREE.Object3D)
        );

        let targetCharacter: Character | null = null;
        let targetPoint: THREE.Vector3 | null = null;

        const characterHit = intersects.find(hit => {
            const ch = this.findAncestorCharacter(hit.object);
            return ch !== null && ch !== this.character;
        });
        if (characterHit) {
            targetCharacter = this.findAncestorCharacter(characterHit.object);
            targetPoint = characterHit.point.clone();
        }

        if (!targetCharacter) {
            const origin = this.rayCaster.ray.origin;
            let bestDist = Infinity;
            for (const ch of this.character.world.characters) {
                if (ch === this.character) { continue; }
                const center = new THREE.Vector3();
                ch.getWorldPosition(center);
                center.y += 0.9;
                const sphere = new THREE.Sphere(center, 0.6);
                const tmp = new THREE.Vector3();
                const p = this.rayCaster.ray.intersectSphere(sphere, tmp);
                if (p) {
                    const d = p.distanceTo(origin);
                    if (d < bestDist && d >= this.rayCaster.near && d <= this.rayCaster.far) {
                        bestDist = d;
                        targetCharacter = ch;
                        targetPoint = p.clone();
                    }
                }
            }
        }

        if (targetCharacter && targetCharacter !== this.character && targetPoint && !targetCharacter.isDead) {
            if (!this.isOccludedByWorld(targetPoint)) {
                const blood = new BloodEffect(this.character.world);
                blood.spawn(targetCharacter, targetPoint);
                targetCharacter.takeDamage(50);
            }
        }
    }

    private isOccludedByWorld(targetPoint: THREE.Vector3): boolean {
        const origin = this.rayCaster.ray.origin.clone();
        const direction = this.rayCaster.ray.direction.clone();
        const targetDist = origin.distanceTo(targetPoint);

        const occluderRay = new THREE.Raycaster(origin, direction, this.rayCaster.near, targetDist - 0.05);

        const characterSet = new Set<THREE.Object3D>(this.character.world.characters as unknown as THREE.Object3D[]);
        const worldObjects = (this.character.world.graphicsWorld.children as THREE.Object3D[]).filter(
            obj => !characterSet.has(obj)
        );

        return occluderRay.intersectObjects(worldObjects, true).length > 0;
    }

    private isDescendantOf(object: THREE.Object3D, ancestor: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current === ancestor) { return true; }
            current = current.parent as THREE.Object3D | null;
        }
        return false;
    }

    private findAncestorCharacter(object: THREE.Object3D): Character | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current instanceof Character) { return current as unknown as Character; }
            current = current.parent as THREE.Object3D | null;
        }
        return null;
    }
}
